import React from 'react';

// --- NEW: derive TP/SL from a group's activities (handles bracket orders + fallbacks)
// Pull TP/SL from a group's activities (prefers bracket â†’ explicit fields)
const deriveLevelsFromActivities = (groupActivities) => {
  let tp = 0, sl = 0;
  console.log("Activity:", groupActivities)
  for (const a of groupActivities) {
    // bracket first
    
    if (a.bracket) {
      const btp = a.bracket.take_profit_price ?? a.bracket.take_profit?.limit_price;
      const bsl = a.bracket.stop_loss_price ?? a.bracket.stop_loss?.stop_price;
      if (btp && +btp > 0) tp = +btp;
      if (bsl && +bsl > 0) sl = +bsl;
    }
    // explicit fields next (from BuildActivity passthrough)
    const candTP = a.target ?? a.tp ?? a.take_profit ?? a.target_price ?? a.tp_price ?? a.limit_price;
    const candSL = a.stop   ?? a.sl ?? a.stop_loss   ?? a.stop_price   ?? a.sl_price;
    if (candTP && +candTP > 0) tp = +candTP;
    if (candSL && +candSL > 0) sl = +candSL;
  }

  return { tp, sl };
};


/**
 * Helper to build position summary from grouped activities
 * Handles complex scenarios like: 0 â†’ 100 â†’ 200 â†’ 100 â†’ 0 (double orders)
 */
const buildPositionsFromActivities = (activities, currentPrice) => {
    if (!activities || activities.length === 0) {
        return [];
    }

    // Group activities by trade group
    const tradeGroups = {};
    activities.forEach((activity) => {
        const groupId = activity.group;
        if (!tradeGroups[groupId]) {
            tradeGroups[groupId] = [];
        }
        tradeGroups[groupId].push(activity);
    });

    const positions = [];

    Object.keys(tradeGroups).forEach((groupId) => {
        const groupActivities = tradeGroups[groupId].sort(
            (a, b) => new Date(a.created_at_gmt) - new Date(b.created_at_gmt)
        );

        const firstActivity = groupActivities[0];
        const lastActivity = groupActivities[groupActivities.length - 1];
        
        // CRITICAL: Always use the LAST activity's open_qty
        // This handles double orders: 0â†’100â†’200â†’100â†’0
        // The last activity shows the final state
        const openQty = lastActivity.open_qty || 0;
        
        // Detailed logging for debugging
        console.log(`ðŸ“Š Group ${groupId} (${firstActivity.symbol}):`);
        console.log(`   Activities: ${groupActivities.length}`);
        groupActivities.forEach((act, idx) => {
            console.log(`   ${idx + 1}. ${act.side} ${act.qty} @ $${act.price} â†’ open_qty: ${act.open_qty}`);
        });
        console.log(`   Final state: open_qty = ${openQty} â†’ ${openQty !== 0 ? 'ACTIVE' : 'CLOSED'}`);

        // Only include if position is still open (last activity shows non-zero)
        if (openQty !== 0) {
            const isLong = firstActivity.side === "buy";
            const entryPrice = parseFloat(firstActivity.price);
            const qty = Math.abs(openQty);
            const symbol = firstActivity.symbol;

            // Calculate unrealized P&L
            const unrealizedPL = currentPrice > 0
                ? isLong
                    ? (currentPrice - entryPrice) * qty
                    : (entryPrice - currentPrice) * qty
                : 0;

            // Calculate hold time
            const entryTime = new Date(firstActivity.created_at_gmt);
            const now = new Date();
            const holdTimeMin = Math.floor((now - entryTime) / 60000);
            const { tp, sl } = deriveLevelsFromActivities(groupActivities);

            positions.push({
                // Keep all activity fields
                ...lastActivity,
                // Position-specific fields
                id: lastActivity.id || lastActivity.a_id,
                a_id: lastActivity.a_id,
                group: groupId,
                symbol,
                isLong,
                side: isLong ? 'buy' : 'sell_short',
                entryPrice,
                currentPrice,
                price: entryPrice, // for compatibility
                qty,
                open_qty: openQty,
                unrealizedPL,
                entryTime: firstActivity.created_at_gmt,
                holdTimeMin,
                // Placeholders - will be enhanced with target/stop from orders
                target: tp || 0,
                stop: sl || 0,
            });
        }
    });

    console.log(`âœ… Built ${positions.length} active positions from ${activities.length} activities`);
    return positions;
};

// Helper function to extract adjustments from liveBars
const extractAdjustments = (liveBars, positionId) => {
    if (!liveBars || !Array.isArray(liveBars) || !positionId) {
        return { adjustments: [], count: 0, hasAdjustments: false };
    }

    const adjustments = [];
    
    liveBars.forEach((bar, index) => {
        // Try multiple fields to match position ID
        const barPositionId = bar.eval_position_id || bar.position_id || bar.a_id || bar.group;
        
        const isMatch = barPositionId == positionId; // Use == for type coercion
        
        if (!isMatch) {
            return;
        }
        
        // Check for ADJUST decision with new values
        if (bar.eval_decision === 'ADJUST') {
            const hasNewTarget = bar.eval_new_target !== undefined && bar.eval_new_target !== null && bar.eval_new_target > 0;
            const hasNewStop = bar.eval_new_stop_loss !== undefined && bar.eval_new_stop_loss !== null && bar.eval_new_stop_loss > 0;
            
            if (hasNewTarget || hasNewStop) {
                const adjustment = {
                    timestamp: bar.t,
                    newTarget: hasNewTarget ? parseFloat(bar.eval_new_target) : null,
                    newStop: hasNewStop ? parseFloat(bar.eval_new_stop_loss) : null,
                    reason: bar.eval_pattern_influence || bar.eval_reasoning || 'Adjustment'
                };
                adjustments.push(adjustment);
            }
        }
    });

    return {
        adjustments,
        count: adjustments.length,
        hasAdjustments: adjustments.length > 0
    };
};

// PositionCard Component with Adjustment Display
const PositionCard = ({ position, onClose, onAdjust, onDetail, currentPrice, liveBars }) => {
    if (!position) {
        return null;
    }

    const {
        symbol,
        side,
        isLong,
        qty,
        open_qty,
        price,
        entryPrice,
        currentPrice: positionCurrentPrice,
        target,
        stop,
        created_at_gmt,
        entryTime,
        group,
        id,
        a_id
    } = position;

    const current = currentPrice || positionCurrentPrice || price || 0;
    const entry = entryPrice || price || 0;
    const quantity = Math.abs(open_qty || qty || 0);
    const positionSide = isLong !== undefined ? (isLong ? 'LONG' : 'SHORT') : (side === 'buy' ? 'LONG' : 'SHORT');

    // Extract adjustments from liveBars
    const positionId = group || id || a_id; // Use group first as it's most reliable
    const { adjustments, count, hasAdjustments } = extractAdjustments(liveBars, positionId);
    
    // Get original values
    const origTargetValue = (
      target ??
      position.take_profit ??
      position.tp ??
      position.target_price ??
      position.tp_price ??
      position.limit_price ??
      0
    );
    
    const origStopValue = (
      stop ??
      position.stop_loss ??
      position.sl ??
      position.stop_price ??
      position.sl_price ??
      0
    );
    
    // Get current values (latest adjustment or original)
    let targetValue = origTargetValue;
    let stopValue = origStopValue;
    
    if (hasAdjustments && adjustments.length > 0) {
        const latestAdjustment = adjustments[adjustments.length - 1];
        if (latestAdjustment.newTarget !== null) {
            targetValue = latestAdjustment.newTarget;
        }
        if (latestAdjustment.newStop !== null) {
            stopValue = latestAdjustment.newStop;
        }
    }
    
    const targetAdjusted = hasAdjustments && (targetValue !== origTargetValue) && targetValue > 0;
    const stopAdjusted = hasAdjustments && (stopValue !== origStopValue) && stopValue > 0;

    // Calculate unrealized P&L
    let unrealizedPL = 0;
    if (positionSide === 'LONG') {
        unrealizedPL = (current - entry) * quantity;
    } else {
        unrealizedPL = (entry - current) * quantity;
    }

    const unrealizedPercent = entry !== 0
        ? ((unrealizedPL / (entry * quantity)) * 100)
        : 0;

    const isPositive = unrealizedPL >= 0;
    const sideClass = positionSide === 'LONG' ? 'long' : 'short';

    // Calculate hold time
    const startTime = new Date(entryTime || created_at_gmt);
    const holdTimeMs = Date.now() - startTime.getTime();
    const holdTimeMinutes = Math.floor(holdTimeMs / 60000);
    const holdTimeHours = Math.floor(holdTimeMinutes / 60);
    const holdTime = holdTimeHours > 0
        ? `${holdTimeHours}h ${holdTimeMinutes % 60}m`
        : `${holdTimeMinutes}m`;

    // Calculate risk/reward ratio (using current adjusted values)
    let riskReward = 'N/A';
    if (targetValue > 0 && stopValue > 0) {
        if (positionSide === 'LONG') {
            const reward = targetValue - entry;
            const risk = entry - stopValue;
            riskReward = risk > 0 ? (reward / risk).toFixed(1) + 'R' : 'N/A';
        } else {
            const reward = entry - targetValue;
            const risk = stopValue - entry;
            riskReward = risk > 0 ? (reward / risk).toFixed(1) + 'R' : 'N/A';
        }
    }

    // Calculate original risk/reward for comparison
    let originalRR = 'N/A';
    if (origTargetValue > 0 && origStopValue > 0) {
        if (positionSide === 'LONG') {
            const reward = origTargetValue - entry;
            const risk = entry - origStopValue;
            originalRR = risk > 0 ? (reward / risk).toFixed(1) + 'R' : 'N/A';
        } else {
            const reward = entry - origTargetValue;
            const risk = origStopValue - entry;
            originalRR = risk > 0 ? (reward / risk).toFixed(1) + 'R' : 'N/A';
        }
    }

    return (
        <div className={`position-card ${sideClass}`}>
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');

        .position-card {
          background: #0C1525;
          border-radius: 10px;
          padding: 14px;
          border: 1px solid #1E2D42;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          font-family: 'DM Mono', monospace;
        }

        .position-card:hover {
          border-color: #2a3d56;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
        }

        .position-card.long {
          border-left: 3px solid #00E896;
        }

        .position-card.short {
          border-left: 3px solid #FF4466;
        }

        .position-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .position-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .side-badge {
          width: 22px;
          height: 22px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 700;
          font-family: 'DM Mono', monospace;
        }

        .side-badge.long {
          background: rgba(0, 232, 150, 0.15);
          color: #00E896;
          border: 1px solid rgba(0, 232, 150, 0.3);
        }

        .side-badge.short {
          background: rgba(255, 68, 102, 0.15);
          color: #FF4466;
          border: 1px solid rgba(255, 68, 102, 0.3);
        }

        .symbol {
          font-size: 14px;
          font-weight: 500;
          color: #E8EDF5;
          letter-spacing: 0.5px;
          font-family: 'DM Mono', monospace;
        }

        .adjustment-badge {
          background: rgba(255, 179, 64, 0.15);
          color: #FFB340;
          font-size: 9px;
          font-weight: 500;
          padding: 2px 6px;
          border-radius: 3px;
          letter-spacing: 0.5px;
          border: 1px solid rgba(255, 179, 64, 0.25);
          font-family: 'DM Mono', monospace;
        }

        .pnl {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 3px;
        }

        .pnl-amount {
          font-size: 15px;
          font-weight: 500;
          font-family: 'DM Mono', monospace;
        }

        .pnl.positive .pnl-amount {
          color: #00E896 !important;
        }

        .pnl.negative .pnl-amount {
          color: #FF4466 !important;
        }

        .pnl-percent {
          font-size: 10px;
          font-weight: 500;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: 'DM Mono', monospace;
        }

        .pnl.positive .pnl-percent {
          background: rgba(0, 232, 150, 0.12);
          color: #00E896;
          border: 1px solid rgba(0, 232, 150, 0.2);
        }

        .pnl.negative .pnl-percent {
          background: rgba(255, 68, 102, 0.12);
          color: #FF4466;
          border: 1px solid rgba(255, 68, 102, 0.2);
        }

        .position-details {
          margin-bottom: 10px;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }

        .detail-row {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .detail-label {
          font-size: 9px;
          color: #6A7D95;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: 'DM Mono', monospace;
        }

        .detail-value {
          font-size: 12px;
          color: #B8C8DC;
          font-weight: 400;
          font-family: 'DM Mono', monospace;
        }

        .position-levels {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid #1E2D42;
          border-radius: 6px;
          padding: 10px;
          margin-bottom: 10px;
          display: flex;
          flex-direction: column;
          gap: 7px;
        }

        .level-row {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-family: 'DM Mono', monospace;
        }

        .level-icon {
          font-size: 12px;
        }

        .level-label {
          color: #6A7D95;
          font-weight: 400;
          min-width: 26px;
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .level-value-container {
          margin-left: auto;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }

        .level-value {
          color: #B8C8DC;
          font-weight: 400;
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
        }

        .level-row.tp .level-value {
          color: #00E896;
        }

        .level-row.sl .level-value {
          color: #FF4466;
        }

        .original-value {
          font-size: 10px;
          color: #6A7D95;
          text-decoration: line-through;
          font-weight: 400;
        }

        .adjustment-arrow {
          color: #FFB340;
          font-size: 9px;
          font-weight: 500;
        }

        .adjusted-value {
          font-size: 12px;
          font-weight: 500;
        }

        .level-row.tp .adjusted-value {
          color: #00E896;
        }

        .level-row.sl .adjusted-value {
          color: #FF4466;
        }

        .adjustment-info {
          font-size: 9px;
          color: #FFB340;
          font-weight: 400;
          display: flex;
          align-items: center;
          gap: 4px;
          letter-spacing: 0.3px;
        }

        .position-metrics {
          display: flex;
          gap: 14px;
          margin-bottom: 10px;
        }

        .metric {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .metric-label {
          font-size: 9px;
          color: #6A7D95;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: 'DM Mono', monospace;
        }

        .metric-value {
          font-size: 11px;
          color: #B8C8DC;
          font-weight: 400;
          padding: 2px 7px;
          background: rgba(255, 255, 255, 0.04);
          border-radius: 3px;
          border: 1px solid #1E2D42;
          font-family: 'DM Mono', monospace;
        }

        .metric-value.adjusted {
          background: rgba(255, 179, 64, 0.10);
          color: #FFB340;
          border: 1px solid rgba(255, 179, 64, 0.25);
        }

        .rr-comparison {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          font-family: 'DM Mono', monospace;
        }

        .rr-original {
          color: #6A7D95;
          text-decoration: line-through;
          font-weight: 400;
        }

        .position-actions {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 6px;
        }

        .action-btn {
          padding: 8px;
          border-radius: 6px;
          border: none;
          font-size: 10px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          font-family: 'DM Mono', monospace;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .close-btn {
          background: rgba(255, 68, 102, 0.12);
          color: #FF4466;
          border: 1px solid rgba(255, 68, 102, 0.3);
        }

        .close-btn:hover {
          background: rgba(255, 68, 102, 0.20);
          border-color: rgba(255, 68, 102, 0.55);
          box-shadow: 0 2px 8px rgba(255, 68, 102, 0.2);
        }

        .adjust-btn {
          background: rgba(0, 200, 255, 0.10);
          color: #00C8FF;
          border: 1px solid rgba(0, 200, 255, 0.25);
        }

        .adjust-btn:hover {
          background: rgba(0, 200, 255, 0.18);
          border-color: rgba(0, 200, 255, 0.45);
          box-shadow: 0 2px 8px rgba(0, 200, 255, 0.15);
        }

        .detail-btn {
          background: rgba(255, 255, 255, 0.04);
          color: #6A7D95;
          border: 1px solid #1E2D42;
        }

        .detail-btn:hover {
          background: rgba(255, 255, 255, 0.07);
          color: #B8C8DC;
          border-color: #2a3d56;
        }
      `}</style>

            <div className="position-header">
                <div className="position-title">
                    <span className={`side-badge ${sideClass}`}>
                        {positionSide === 'LONG' ? 'L' : 'S'}
                    </span>
                    <span className="symbol">{symbol}</span>
                    {hasAdjustments && (
                        <span className="adjustment-badge">
                            {count}Ã— ADJ
                        </span>
                    )}
                </div>
                <div className={`pnl ${isPositive ? 'positive' : 'negative'}`}>
                    <span className="pnl-amount" style={{ color: isPositive ? '#00E896' : '#FF4466' }}>
                        {isPositive ? '+' : '-'}${Math.abs(unrealizedPL).toFixed(0)}
                    </span>
                    <span className="pnl-percent">
                        {isPositive ? '▲' : '▼'}{Math.abs(unrealizedPercent).toFixed(2)}%
                    </span>
                </div>
            </div>

            <div className="position-details">
                <div className="detail-grid">
                    <div className="detail-row">
                        <span className="detail-label">Qty:</span>
                        <span className="detail-value">{quantity}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Entry:</span>
                        <span className="detail-value">${parseFloat(entry).toFixed(2)}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Current:</span>
                        <span className="detail-value">${parseFloat(current).toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {(targetValue > 0 || stopValue > 0) && (
                <div className="position-levels">
                    {targetValue > 0 && (
                        <div className="level-row tp">
                            <span className="level-icon">🎯</span>
                            <span className="level-label">TP:</span>
                            <div className="level-value-container">
                                {targetAdjusted ? (
                                    <>
                                        <div className="level-value">
                                            <span className="original-value">${parseFloat(origTargetValue).toFixed(2)}</span>
                                            <span className="adjustment-arrow">â†’</span>
                                            <span className="adjusted-value">${parseFloat(targetValue).toFixed(2)}</span>
                                        </div>
                                        <span className="adjustment-info">
                                            âš¡ Adjusted
                                        </span>
                                    </>
                                ) : (
                                    <div className="level-value">
                                        ${parseFloat(targetValue).toFixed(2)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {stopValue > 0 && (
                        <div className="level-row sl">
                            <span className="level-icon">🛡️</span>
                            <span className="level-label">SL:</span>
                            <div className="level-value-container">
                                {stopAdjusted ? (
                                    <>
                                        <div className="level-value">
                                            <span className="original-value">${parseFloat(origStopValue).toFixed(2)}</span>
                                            <span className="adjustment-arrow">â†’</span>
                                            <span className="adjusted-value">${parseFloat(stopValue).toFixed(2)}</span>
                                        </div>
                                        <span className="adjustment-info">
                                            âš¡ Adjusted
                                        </span>
                                    </>
                                ) : (
                                    <div className="level-value">
                                        ${parseFloat(stopValue).toFixed(2)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="position-metrics">
                <div className="metric">
                    <span className="metric-label">Risk:</span>
                    {(targetAdjusted || stopAdjusted) && originalRR !== riskReward ? (
                        <div className="rr-comparison">
                            <span className="rr-original">{originalRR}</span>
                            <span className="adjustment-arrow">â†’</span>
                            <span className={`metric-value adjusted`}>{riskReward}</span>
                        </div>
                    ) : (
                        <span className="metric-value">{riskReward}</span>
                    )}
                </div>
                <div className="metric">
                    <span className="metric-label">Hold:</span>
                    <span className="metric-value">{holdTime}</span>
                </div>
            </div>

            <div className="position-actions">
                <button className="action-btn close-btn" onClick={() => onClose && onClose(position)}>
                    Close
                </button>
                <button className="action-btn adjust-btn" onClick={() => onAdjust && onAdjust(position)}>
                    Adjust
                </button>
                <button className="action-btn detail-btn" onClick={() => onDetail && onDetail(position)}>
                    Detail
                </button>
            </div>
        </div>
    );
};

// ActivePositions Container Component - Uses activities directly
const ActivePositions = ({ 
    activities,      // NEW: Direct from buildActivities()
    positions,       // OLD: Keep for backward compatibility
    onClose, 
    onAdjust, 
    onDetail, 
    onAddPosition, 
    currentPrice, 
    liveBars 
}) => {
    // PREFER positions from TradingCommand (which has all enriched data)
    // Only rebuild from activities if positions not provided
    const activePositions = React.useMemo(() => {
        if (positions && positions.length > 0) {
            console.log('🔍 ActivePositions: Filtering from TradingCommand positions');
            console.log(`   Total positions received: ${positions.length}`);
            
            const filtered = positions.filter(p => {
                // CRITICAL: Check open_qty explicitly
                // Don't fall back to qty because open_qty=0 is valid for closed positions
                const openQty = p.open_qty ?? 0; // Use nullish coalescing to preserve 0
                const isActive = openQty !== 0;
                
                console.log(`   ${isActive ? '✅ KEEP' : '❌ FILTER'}: ${p.symbol} group=${p.group} open_qty=${p.open_qty} → ${isActive ? 'ACTIVE' : 'CLOSED'}`);
                
                return isActive;
            });
            
            console.log(`   📊 Result: ${filtered.length} active of ${positions.length} total positions`);
            return filtered;
        } else if (activities && activities.length > 0) {
            console.log('⚠️ Fallback: Building active positions from activities');
            return buildPositionsFromActivities(activities, currentPrice);
        }
        return [];
    }, [positions, activities, currentPrice]);


    const handleAddPosition = () => {
        if (onAddPosition) {
            onAddPosition();
        } else {
            console.log('Add new position');
        }
    };

    return (
        <div className="active-positions-container">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');

        .active-positions-container {
          width: 100%;
          height: 100%;
          background: #08101E;
          padding: 16px;
          overflow-y: auto;
          font-family: 'DM Mono', monospace;
          display: flex;
          flex-direction: column;
        }

        .active-positions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #1E2D42;
          flex-shrink: 0;
        }

        .active-positions-header h3 {
          margin: 0;
          font-size: 11px;
          font-weight: 500;
          color: #E8EDF5;
          letter-spacing: 1px;
          text-transform: uppercase;
          font-family: 'DM Mono', monospace;
        }

        .position-count {
          background: rgba(68, 136, 255, 0.12);
          color: #4488FF;
          padding: 3px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          font-family: 'DM Mono', monospace;
          border: 1px solid rgba(68, 136, 255, 0.25);
        }

        .positions-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
          flex: 1;
          overflow-y: auto;
        }

        .add-position-btn {
          width: 100%;
          padding: 12px;
          background: rgba(0, 200, 255, 0.06);
          border: 1px dashed rgba(0, 200, 255, 0.25);
          border-radius: 8px;
          color: #00C8FF;
          font-size: 11px;
          font-weight: 500;
          font-family: 'DM Mono', monospace;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
          letter-spacing: 0.5px;
        }

        .add-position-btn:hover {
          background: rgba(0, 200, 255, 0.10);
          border-color: rgba(0, 200, 255, 0.45);
        }

        .active-positions-container::-webkit-scrollbar {
          width: 4px;
        }

        .active-positions-container::-webkit-scrollbar-track {
          background: #08101E;
        }

        .active-positions-container::-webkit-scrollbar-thumb {
          background: #1E2D42;
          border-radius: 2px;
        }

        .active-positions-container::-webkit-scrollbar-thumb:hover {
          background: #2a3d56;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #6A7D95;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .empty-state-icon {
          font-size: 36px;
          margin-bottom: 12px;
          opacity: 0.5;
        }

        .empty-state-text {
          font-size: 12px;
          margin-bottom: 6px;
          color: #B8C8DC;
          font-family: 'DM Mono', monospace;
        }

        .empty-state-subtext {
          font-size: 10px;
          color: #6A7D95;
          font-family: 'DM Mono', monospace;
        }
      `}</style>

            <div className="active-positions-header">
                <h3>ACTIVE POSITIONS</h3>
                <span className="position-count">{activePositions.length}</span>
            </div>

            {activePositions.length > 0 ? (
                <div className="positions-list">
                    {activePositions.map((position, index) => (
                        <PositionCard
                            key={position.id || position.a_id || position.group || index}
                            position={position}
                            currentPrice={currentPrice}
                            liveBars={liveBars}
                            onClose={onClose}
                            onAdjust={onAdjust}
                            onDetail={onDetail}
                        />
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">🗄️</div>
                    <div className="empty-state-text">No active positions</div>
                    <div className="empty-state-subtext">Add a position to start trading</div>
                </div>
            )}

            <button className="add-position-btn" onClick={handleAddPosition}>
                + Add Position
            </button>
        </div>
    );
};

export default ActivePositions;